import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";

export default function StepView({ step, value, onChange, onNext, onBack, index, total }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if ((step.type === "money" || step.type === "number") && inputRef.current) {
      inputRef.current.focus();
    }
  }, [step.id, step.type]);

  const canContinue =
    step.type === "choice" ? true : value !== undefined && value !== null && value !== "";

  const handleMoneyChange = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, "");
    onChange(digits === "" ? "" : Number(digits));
  };

  const displayMoney =
    value === "" || value === undefined || value === null
      ? ""
      : Number(value).toLocaleString("en-AU");

  return (
    <motion.div
      key={step.id}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-2xl"
    >
      <div className="flex items-center gap-4 mb-6">
        <span className="eyebrow text-brand">{step.section}</span>
        <span className="eyebrow text-muted-foreground">
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
      </div>

      {step.note && (
        <p className="text-sm text-muted-foreground leading-relaxed mb-6 border-l-2 border-brand pl-4 max-w-xl">
          {step.note}
        </p>
      )}

      <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.02] tracking-tight mb-8">
        {step.question}
      </h2>

      {step.help && (
        <p className="text-muted-foreground mb-8 -mt-3 max-w-xl">{step.help}</p>
      )}

      {step.type === "choice" && (
        <div className="grid gap-3" data-testid={`step-${step.key}-options`}>
          {step.options.map((opt) => {
            const active = value === opt.value;
            return (
              <button
                key={opt.value}
                data-testid={`option-${step.key}-${opt.value}`}
                onClick={() => {
                  onChange(opt.value);
                  setTimeout(onNext, 180);
                }}
                className={`group flex items-center justify-between text-left px-6 py-5 border transition-colors duration-200 ${
                  active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-border hover:border-brand"
                }`}
              >
                <span className="text-lg md:text-xl">{opt.label}</span>
                <span
                  className={`grid place-items-center w-7 h-7 rounded-full border transition-colors ${
                    active
                      ? "bg-brand border-brand text-white"
                      : "border-border group-hover:border-brand"
                  }`}
                >
                  {active ? <Check size={15} /> : <ArrowRight size={14} className="opacity-40 group-hover:opacity-100" />}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {(step.type === "money" || step.type === "number") && (
        <div>
          <div className="flex items-baseline gap-2">
            {step.type === "money" && (
              <span className="font-serif text-4xl md:text-6xl text-muted-foreground">$</span>
            )}
            <input
              ref={inputRef}
              data-testid={`input-${step.key}`}
              inputMode="numeric"
              value={step.type === "money" ? displayMoney : value ?? ""}
              onChange={
                step.type === "money"
                  ? handleMoneyChange
                  : (e) => onChange(e.target.value.replace(/[^\d]/g, ""))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && canContinue) onNext();
              }}
              placeholder={step.placeholder || "0"}
              className="editorial-input text-4xl md:text-6xl py-3"
            />
          </div>

          <div className="flex items-center gap-4 mt-10">
            {index > 0 && (
              <button
                data-testid="step-back-button"
                onClick={onBack}
                className="grid place-items-center w-12 h-12 rounded-full border border-border hover:border-brand transition-colors"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <button
              data-testid="step-continue-button"
              disabled={!canContinue}
              onClick={onNext}
              className="group inline-flex items-center gap-3 rounded-full bg-brand text-brand-foreground pl-7 pr-6 py-4 eyebrow disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary transition-colors duration-300"
            >
              Continue
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      )}

      {step.type === "choice" && index > 0 && (
        <button
          data-testid="step-back-button"
          onClick={onBack}
          className="mt-8 inline-flex items-center gap-2 eyebrow text-muted-foreground hover:text-brand transition-colors"
        >
          <ArrowLeft size={14} /> Back
        </button>
      )}
    </motion.div>
  );
}
